import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { portfolioData } from '../../data/portfolio';

const EducationSection = styled.section`
  padding: 80px 0 80px 48px;

  @media (max-width: 1080px) {
    padding: 80px 24px;
  }
`;

const SectionContent = styled.div`
  max-width: 900px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(24px, 3vw, 32px);
  text-align: left;
  margin: 0 0 48px 0;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
`;

const CardAccent = styled.div<{ status: string }>`
  height: 4px;
  border-radius: 12px 12px 0 0;
  background: ${({ status }) =>
    status === 'current'
      ? 'linear-gradient(90deg, #22c55e, #16a34a)'
      : 'linear-gradient(90deg, #7dd3fc, #3b82f6)'};
`;

const Card = styled(motion.div)`
  background: var(--clr-card-bg);
  border: 1px solid var(--clr-accent-border);
  border-top: none;
  border-radius: 0 0 14px 14px;
  padding: 24px;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CardWrapper = styled(motion.div)`
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  &:hover ${Card} {
    border-color: var(--clr-accent-border-h);
  }
`;

const DegreeIcon = styled.div<{ status: string }>`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ status }) => (status === 'current' ? '#22c55e' : '#7dd3fc')};
  background: ${({ status }) =>
    status === 'current'
      ? 'rgba(34, 197, 94, 0.1)'
      : 'rgba(125, 211, 252, 0.08)'};
  border: 1px solid ${({ status }) =>
    status === 'current'
      ? 'rgba(34, 197, 94, 0.22)'
      : 'rgba(125, 211, 252, 0.18)'};
  flex-shrink: 0;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const HeaderText = styled.div`
  flex: 1;
`;

const Degree = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 4px 0;
  line-height: 1.35;
`;

const Institution = styled.div`
  color: ${({ theme }) => theme.colors.accent};
  font-size: 13px;
  font-weight: 600;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const Duration = styled.span`
  color: var(--clr-text-dim);
  font-size: 12px;
`;

const StatusBadge = styled.span<{ status: string }>`
  background: ${({ status }) =>
    status === 'current'
      ? 'rgba(34, 197, 94, 0.15)'
      : 'rgba(125, 211, 252, 0.1)'};
  color: ${({ status }) => (status === 'current' ? '#22c55e' : '#7dd3fc')};
  border: 1px solid ${({ status }) =>
    status === 'current'
      ? 'rgba(34, 197, 94, 0.3)'
      : 'rgba(125, 211, 252, 0.2)'};
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Description = styled.p`
  color: var(--clr-text-dim);
  font-size: 13px;
  line-height: 1.5;
  margin: 0;
`;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const degreeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
    <path d="M6 12v5c3 3 9 3 12 0v-5"/>
  </svg>
);

const Education: React.FC = () => {
  const { t } = useTranslation();
  return (
    <EducationSection id="education">
      <SectionContent>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <SectionTitle>{t('education.sectionTitle')}</SectionTitle>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <Grid>
              {portfolioData.education.map((edu) => (
                <CardWrapper
                  key={edu.id}
                  variants={itemVariants}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                >
                  <CardAccent status={edu.status} />
                  <Card>
                    <CardHeader>
                      <DegreeIcon status={edu.status}>
                        {degreeIcon()}
                      </DegreeIcon>
                      <HeaderText>
                        <Degree>{t(`education.entries.${edu.id}.degree`, edu.degree)}</Degree>
                        <Institution>{edu.institution}</Institution>
                      </HeaderText>
                    </CardHeader>

                    <Meta>
                      <Duration>{edu.duration} · {edu.location}</Duration>
                      <StatusBadge status={edu.status}>
                        {edu.status === 'current' ? t('education.statusCurrent') : t('education.statusCompleted')}
                      </StatusBadge>
                    </Meta>

                    {edu.description && (
                      <Description>{t(`education.entries.${edu.id}.description`, edu.description)}</Description>
                    )}
                  </Card>
                </CardWrapper>
              ))}
            </Grid>
          </motion.div>
      </SectionContent>
    </EducationSection>
  );
};

export default Education;
